import { tv } from 'tailwind-variants'

const variantClasses = {
  // Inter font
  body1: 'text-[16px] leading-[150%] tracking-[0.15px] font-normal font-inter',
  body2: 'text-[14px] leading-[143%] tracking-[0.17px] font-medium font-inter',
  body3: 'text-[12px] leading-[143%] tracking-[0.15px] font-medium font-inter',
  'body3/cap': 'text-[12px] leading-[143%] tracking-[0.17px] font-medium uppercase font-inter',
  body4: 'text-[10px] leading-[143%] tracking-[0.17px] font-medium font-inter',
  'body4/cap': 'text-[10px] leading-[120%] tracking-[0.17px] font-medium uppercase font-inter',

  subtitle1: 'text-[16px] leading-[120%] tracking-[0.15px] font-semibold font-inter',
  subtitle2: 'text-[14px] leading-[140%] tracking-[0.1px] font-semibold font-inter',
  subtitle3: 'text-[12px] leading-[157%] tracking-[0.1px] font-semibold font-inter',

  overline: 'text-[12px] leading-[120%] tracking-[1px] font-normal font-inter',

  badge: 'text-[8px] leading-[250%] tracking-[0.14px] font-medium uppercase font-inter',
  'badge/label': 'text-[8px] leading-[120%] tracking-[0.14px] font-medium uppercase font-inter',
  'input/label': 'text-[12px] leading-[100%] tracking-[0.15px] font-normal font-inter',
  'input/value/small': 'text-[14px] leading-[128.571%] tracking-[0.1px] font-medium font-inter',
  'input/value/medium': 'text-[16px] leading-[150%] tracking-[0.15px] font-medium font-inter',
  'input/helper': 'text-[10px] leading-[166%] tracking-[0px] font-normal font-inter',
  'tooltip/label': 'text-[12px] leading-[120%] font-normal font-inter',

  // Chip typography (internal — not exposed on Typography component)
  'chip/label': 'text-[12px] tracking-[0.16px] font-normal font-inter',
  // Mobile-only large chip label (Figma style `chip/label-large`, shared by every
  // Size=Large chip in set 6588:47683) — 16px on an 18px line, so the glyphs stay
  // optically centred in the 48px chip instead of inheriting a 24px default.
  'chip/label-large': 'text-[16px] leading-[18px] tracking-[0.16px] font-normal font-inter',
  'chip/sublabel': 'text-[10px] tracking-[0.4px] font-normal font-inter',

  // Button typography
  'button/small': 'text-[12px] leading-[143%] tracking-[0.1px] font-semibold font-inter',
  'button/medium': 'text-[14px] leading-[143%] tracking-[0.2px] font-semibold font-inter',
  'button/large': 'text-[16px] leading-[150%] tracking-[0.2px] font-semibold font-inter',

  // Table typography
  'table/header-xs':
    'text-[10px] leading-[240%] tracking-[0.17px] font-medium uppercase font-poppins',

  // Poppins font
  caption: 'font-heading text-[12px] leading-[166%] tracking-[0.4px] font-normal',
  h1: 'font-heading text-[72px] leading-[116.7%] tracking-[-1.5px] font-semibold',
  h2: 'font-heading text-[48px] leading-[120%] tracking-[-0.5px] font-semibold',
  h3: 'font-heading text-[40px] leading-[116.7%] font-semibold',
  h4: 'font-heading text-[32px] leading-[123.5%] tracking-[0.25px] font-semibold',
  h5: 'font-heading text-[24px] leading-[120%] font-semibold',
  h6: 'font-heading text-[20px] leading-[140%] tracking-[0.15px] font-semibold',
} as const

const colorClasses = {
  inherit: 'text-[inherit]',
  primary: 'text-primary-main',
  'primary.contrastText': 'text-primary-contrastText',
  secondary: 'text-secondary-main',
  warning: 'text-warning-main',
  error: 'text-error-main',
  'error.contrastText': 'text-error-contrastText',
  info: 'text-info-main',
  'info.light': 'text-info-light',

  'text.primary': 'text-text-primary',
  'text.secondary': 'text-text-secondary',
  'text.disabled': 'text-text-disabled',
  'text.ghost': 'text-text-ghost',
  'text.hover': 'text-text-hover',
  'action.active': 'text-text-action-active',
  'table.header': 'text-table-header',
} as const

export type TypographyVariant = keyof typeof variantClasses
export type TypographyColor = keyof typeof colorClasses

export const typographyVariants = tv({
  variants: {
    variant: variantClasses,
    color: colorClasses,
    gutterBottom: {
      true: 'mb-[0.35em]',
      false: '',
    },
    noWrap: {
      true: 'truncate',
      false: '',
    },
  },
  defaultVariants: {
    variant: 'body1',
    color: 'inherit',
    gutterBottom: false,
    noWrap: false,
  },
})

export const typography = variantClasses
